import React, { Component } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { connect } from 'react-redux';
import './TypeHistoryPage.css';

// Redux actions
import GetTypesActions from '../../../redux/GetTypesRedux';

// Icons
import { t } from 'i18next';

interface TypeHistoryPageProps {
  navigate: NavigateFunction;
  // Redux props
  currentSettings: any;
  getTypesFetching: boolean;
  getTypesSuccess: boolean;
  getTypesError: boolean;
  listTypes: any;
  // Redux actions
  getTypes: (children_id: string) => void;
}

interface TypeHistoryPageState {
  selected: string | null;
  // Track if this is initial load
  isInitialLoad: boolean;
}

class TypeHistoryPage extends Component<TypeHistoryPageProps, TypeHistoryPageState> {
  constructor(props: TypeHistoryPageProps) {
    super(props);
    
    // Get children_id from currentSettings
    const children_id = this.getChildrenId();
    
    // Fetch types on component mount
    if (children_id) {
      this.props.getTypes(children_id);
    }
    
    this.state = {
      selected: null,
      isInitialLoad: true,
    };
  }

  componentDidMount() {
    console.log('TypeHistoryPage mounted');
  }

  componentDidUpdate(prevProps: TypeHistoryPageProps) {
    // Handle get types success/error
    if (!this.props.getTypesFetching) {
      if (
        this.props.getTypesSuccess &&
        this.props.getTypesSuccess !== prevProps.getTypesSuccess
      ) {
        // Mark initial load as complete
        this.setState({ isInitialLoad: false });
        
        // Auto-select first type if none selected (use unique types)
        const uniqueTypes = this.getUniqueTypes();
        if (uniqueTypes.length > 0 && !this.state.selected) {
          this.setState({ selected: uniqueTypes[0].id });
        }
      }
    }

    if (
      this.props.getTypesError &&
      this.props.getTypesError !== prevProps.getTypesError
    ) {
      // Mark initial load as complete even on error
      this.setState({ 
        isInitialLoad: false
      });
    }
  }

  getChildrenId(): string | null {
    // Try multiple sources for children_id
    let children_id = null;
    
    // 1. Try currentSettings.child.salesforce_contact_id
    if (this.props.currentSettings?.child?.salesforce_contact_id) {
      children_id = this.props.currentSettings.child.salesforce_contact_id;
    }
    
    // 2. Try currentSettings.child.id if salesforce_contact_id is not available
    else if (this.props.currentSettings?.child?.id) {
      children_id = this.props.currentSettings.child.id;
    }
    
    if (!children_id) {
      console.error('TypeHistoryPage: No valid children_id found');
    }
    
    return children_id;
  }

  // Filter out duplicate IDs from listTypes.data
  getUniqueTypes(): any[] {
    const { listTypes } = this.props;
    
    if (!listTypes?.data || !Array.isArray(listTypes.data)) {
      return [];
    }

    const seenIds = new Set<string | number>();
    const uniqueTypes: any[] = [];

    listTypes.data.forEach((type: any) => {
      if (type?.id && !seenIds.has(type.id)) {
        seenIds.add(type.id);
        uniqueTypes.push(type);
      }
    });

    return uniqueTypes;
  }

  _onNextPress() {
    if (this.state.selected) {
      this.props.navigate(`/calendar/leave-request-history?type_id=${this.state.selected}`);
    }
  }

  render() {
    const { selected } = this.state;
    const { 
      getTypesFetching, 
      getTypesSuccess, 
      listTypes 
    } = this.props;

    // Show loader when initially loading OR when fetching
    const shouldShowLoader = this.state.isInitialLoad || getTypesFetching;

    // Filter out duplicate IDs - exclude duplicate rows
    const uniqueTypes = this.getUniqueTypes();
    const hasUniqueTypes = uniqueTypes.length > 0;

    return (
      <div className="typehistory-page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              <button 
                className="header-button" 
                onClick={() => this.props.navigate(-1)}
              >
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path>
                </svg>
              </button>
              <h1 className="header-title">
                {String(t?.('leave-request-history:service-type') || 'Service Type')}
              </h1>
            </div>
            <div className="header-right">
              <button
                className="header-button next-button"
                onClick={() => this._onNextPress()}
                disabled={!selected || shouldShowLoader}
              >
                <span>
                  {String(t?.('select-date-and-time-screen:header.right') || 'Next')}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="page-content">
          {shouldShowLoader ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{String(t?.('typehistory:loading') || 'Loading service types...')}</p>
            </div>
          ) : getTypesSuccess && listTypes?.data && listTypes.data.length > 0 && hasUniqueTypes ? (
            <div className="service-type-list">
              {uniqueTypes.map((type: any) => (
                <div 
                  key={type.id} 
                  className="service-type-item"
                  onClick={() => {
                    this.setState({ selected: type.id });
                  }}
                >
                  <div className="service-type-content">
                    <div className="service-type-info">
                      <span className="service-type-name">
                        {type.name}
                      </span>
                    </div>
                    
                    {/* Radio Button Selection */}
                    <div className="radio-selection">
                      {selected === type.id ? (
                        <span className="radio-selected"></span>
                      ) : (
                        <span className="radio-unselected"></span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-types">
              <p>{String(t?.('typehistory:no-types') || 'No service types available')}</p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

// Redux mapping
const mapStateToProps = (state: any) => {
  return {
    currentSettings: state.currentSettings,
    getTypesFetching: state.getTypes.fetching,
    getTypesSuccess: state.getTypes.success,
    getTypesError: state.getTypes.error,
    listTypes: state.getTypes.listTypes,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    getTypes: (children_id: string) => dispatch(GetTypesActions.getTypesRequest(children_id)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TypeHistoryPage);
